'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

interface TruncateContentProps {
	children: React.ReactNode
	className?: string
	dataTruncateLength?: string
	dataTruncateShow?: string
	dataTruncateHide?: string
}

// Globálna funkcia na reset všetkých TruncateContent komponentov
declare global {
	interface Window {
		resetAllTruncateContent: () => void
	}
}

if (typeof window !== 'undefined') {
	window.resetAllTruncateContent = () => {
		// Táto funkcia sa volá z Swiper eventu
		window.dispatchEvent(new CustomEvent('truncate-reset'))
	}
}

export const TruncateContent: React.FC<TruncateContentProps> = ({
	children,
	className = '',
	dataTruncateLength = '300',
	dataTruncateShow = 'viac',
	dataTruncateHide = 'menej'
}) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const [shouldTruncate, setShouldTruncate] = useState(false)
	const [truncatedText, setTruncatedText] = useState('')
	const contentRef = useRef<HTMLDivElement>(null)
	const maxLength = parseInt(dataTruncateLength, 10)

	// Reset expandovaného stavu keď sa zmení obsah
	useEffect(() => {
		setIsExpanded(false)
	}, [children])

	// Reset keď sa volá globálna funkcia (pri prechode na nový slide)
	useEffect(() => {
		// Only add event listener on client side
		if (typeof window === 'undefined') return

		const handleReset = () => {
			setIsExpanded(false)
		}

		window.addEventListener('truncate-reset', handleReset)
		return () => window.removeEventListener('truncate-reset', handleReset)
	}, [])

	// Výpočet textu na zobrazenie
	useEffect(() => {
		if (!contentRef.current) return

		const textContent = contentRef.current.textContent || ''
		const needsTruncation = textContent.length > maxLength

		setShouldTruncate(needsTruncation)

		if (needsTruncation) {
			setTruncatedText(textContent.slice(0, maxLength) + '...')
		}
	}, [children, maxLength])

	const handleButtonClick = useCallback((e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		setIsExpanded(prev => {
			return !prev
		})
	}, [])

	return (
		<div className={className}>
			{/* Skrytý div pre meranie dĺžky */}
			<div ref={contentRef} style={{ display: 'none' }}>
				{children}
			</div>

			{/* Zobrazený obsah */}
			{shouldTruncate ? (
				<div>
					{isExpanded ? (
						// Zobrazí celý obsah s formátovaním
						<div>{children}</div>
					) : (
						// Zobrazí skrátený text
						<div>{truncatedText}</div>
					)}
				</div>
			) : (
				// Ak nepotrebuje skracovanie, zobrazí celý obsah
				<div>{children}</div>
			)}

			{/* Tlačidlo */}
			{shouldTruncate && (
				<button
					type='button'
					onClick={handleButtonClick}
					onMouseDown={e => {
						e.preventDefault()
						e.stopPropagation()
					}}
					onTouchStart={e => {
						e.stopPropagation()
					}}
					className='hover:font-semibold transition-all duration-200 cursor-pointer bg-transparent border-0 p-0 m-0 mt-5 text-inherit underline'
				>
					{isExpanded ? dataTruncateHide : dataTruncateShow}
				</button>
			)}
		</div>
	)
}
